import { cn } from '@spiaggeit/spit-ui'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DangerousHtml } from '../DangerousHtml/DangerousHtml'

const clampMap = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
} as const

interface ExpandedTextProps {
  clamp?: keyof typeof clampMap
  containerClassName?: string
  text: string
  label?: string
}

export function ExpandedText(props: ExpandedTextProps) {
  const { t } = useTranslation()
  const textId = useId()

  const [isExpandEnabled, setIsExpandEnabled] = useState(false)
  const [isTextExpanded, setIsTextExpanded] = useState(false)

  const textRef = useRef<HTMLParagraphElement | null>(null)

  useEffect(() => {
    if (!textRef.current) {
      return
    }

    setIsTextExpanded(false)
    setIsExpandEnabled(
      textRef.current.scrollHeight > textRef.current.clientHeight
    )
  }, [props.text])

  const toggleTextExpand = useCallback(() => {
    setIsTextExpanded((isExpanded) => !isExpanded)
  }, [])

  const buttonLabelI18nKey = isTextExpanded
    ? 'common.showLess'
    : 'common.readMore'

  const buttonTooltipI18nKey = isTextExpanded
    ? 'common.hideExtraContent'
    : 'common.showHiddenContent'

  const clampClass = clampMap[props.clamp ?? 1]

  return (
    <div className={props.containerClassName}>
      <p
        className={cn('mr-2 text-secondary', {
          [clampClass]: !isTextExpanded,
        })}
        ref={textRef}
        {...(!isTextExpanded && {
          title: props.text,
        })}
      >
        {props.label && (
          <span className="font-bold text-black">{props.label}:&nbsp;</span>
        )}
        <DangerousHtml html={props.text.replace(/\n/g, '<br />')} />
      </p>

      {isExpandEnabled ? (
        <button
          aria-controls={textId}
          aria-expanded={isTextExpanded}
          className="mt-2 font-bold leading-5 text-blue-500"
          onClick={toggleTextExpand}
          title={t(buttonTooltipI18nKey)}
          type="button"
        >
          {t(buttonLabelI18nKey)}
        </button>
      ) : null}
    </div>
  )
}
